import { useNavigate } from 'react-router-dom';

import { ButtonBase, Card, CardContent, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconMicrophone, IconMicrophoneOff, IconPhoneOff, IconUser } from '@tabler/icons';

import useTwilioCall from 'hooks/useTwilioCall';

import AudioDevicePicker from './AudioDevicePicker';

// Floating call card mounted at MainLayout. Survives navigation while a call is
// active so the provider can leave the patient page (in theory) without losing
// call controls. The navigation chrome itself is locked elsewhere — see the
// in-call swap on Header.
//
// Reads everything from the slice via useTwilioCall — does not refetch the
// patient. The patientSnapshot field (firstName/lastName/phoneNumber) was
// captured at startCall time precisely so the card can render without depending
// on the patient view being mounted.

function formatDuration(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function statusLabel(status) {
    switch (status) {
        case 'fetchingToken':
            return 'Preparing call…';
        case 'connecting':
            return 'Connecting…';
        case 'ringing':
            return 'Ringing…';
        case 'inCall':
            return 'In call';
        case 'error':
            return 'Call failed';
        default:
            return '';
    }
}

const CallCard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const {
        isActive,
        status,
        error,
        terminalMessage,
        muted,
        durationSeconds,
        patientSnapshot,
        toNumber,
        clinicId,
        patientId,
        hangup,
        setMuted,
        resetCallState
    } = useTwilioCall();

    if (!isActive && status !== 'error' && status !== 'remoteEnded') {
        return null;
    }

    const fullName = patientSnapshot ? `${patientSnapshot.firstName ?? ''} ${patientSnapshot.lastName ?? ''}`.trim() : '';
    const displayNumber = patientSnapshot?.phoneNumber ?? toNumber ?? '';

    const onPatientClick = () => {
        navigate(`/clinic/${clinicId}/patient/${patientId}/edit/overview/main`);
    };

    const onMuteToggle = () => {
        setMuted(!muted);
    };

    const isError = status === 'error';
    const isRemoteEnded = status === 'remoteEnded';
    const isTerminal = isError || isRemoteEnded;

    const onHangup = () => {
        if (isTerminal) {
            resetCallState();
        } else {
            hangup();
        }
    };

    return (
        <Card
            elevation={6}
            sx={{
                position: 'fixed',
                bottom: 24,
                // App snackbars default to bottom-right (store/slices/snackbar.js).
                // Anchor the call card to the opposite corner so toasts and the
                // card never occupy the same space.
                left: 24,
                width: 320,
                zIndex: (t) => t.zIndex.snackbar + 1,
                borderLeft: `4px solid ${
                    // eslint-disable-next-line no-nested-ternary
                    isError ? theme.palette.error.main : isRemoteEnded ? theme.palette.grey[500] : theme.palette.primary.main
                }`
            }}
        >
            <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography
                        variant="overline"
                        // eslint-disable-next-line no-nested-ternary
                        color={isError ? 'error' : isRemoteEnded ? 'text.secondary' : 'primary'}
                    >
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {isError ? 'Call failed' : isRemoteEnded ? terminalMessage ?? 'Call ended' : statusLabel(status)}
                    </Typography>
                    {status === 'inCall' && (
                        <Typography variant="overline" color="text.secondary">
                            {formatDuration(durationSeconds)}
                        </Typography>
                    )}
                </Stack>

                <ButtonBase
                    onClick={onPatientClick}
                    focusRipple
                    sx={{
                        mt: 0.5,
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        borderRadius: 1,
                        p: 0.5,
                        mx: -0.5
                    }}
                    aria-label={fullName ? `Open patient page for ${fullName}` : 'Open patient page'}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconUser size={18} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {fullName || 'Patient'}
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 3.25 }}>
                        {displayNumber}
                    </Typography>
                </ButtonBase>

                {isError && error && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {error}
                    </Typography>
                )}

                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                    {!isTerminal && <AudioDevicePicker />}
                    {!isTerminal && (
                        <Tooltip title={muted ? 'Unmute' : 'Mute'}>
                            <span>
                                <IconButton
                                    onClick={onMuteToggle}
                                    color={muted ? 'warning' : 'default'}
                                    disabled={status !== 'inCall'}
                                    aria-label={muted ? 'Unmute' : 'Mute'}
                                >
                                    {muted ? <IconMicrophoneOff size={20} /> : <IconMicrophone size={20} />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title={isTerminal ? 'Dismiss' : 'Hang up'}>
                        <IconButton
                            onClick={onHangup}
                            sx={{
                                bgcolor: theme.palette.error.main,
                                color: theme.palette.error.contrastText,
                                '&:hover': { bgcolor: theme.palette.error.dark }
                            }}
                            aria-label={isTerminal ? 'Dismiss' : 'Hang up'}
                        >
                            <IconPhoneOff size={20} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default CallCard;
